import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Trash2 } from 'lucide-react';

import SearchBar from 'components/SearchBar';
import SimpleModal from 'components/SimpleModal';
import { useNotification } from 'hooks/useNotification';
import { selectTeamMembers } from 'selectors';

import { useListSuppressedEmailsQuery } from 'AwsModule/query/hooks/useListSuppressedEmailsQuery';
import { useDeleteSuppressedEmailMutation } from 'AwsModule/query/hooks/useDeleteSuppressedEmailMutation';
import { SuppressedEmail } from 'AwsModule/api/listSuppressedEmailsApi';

// `mosaicapp.com` shows up on essentially every team's member list because of
// our internal/integration accounts, but suppressed mosaic emails aren't
// actionable from this tab — exclude it. Fallback: if there are no other
// domains, query mosaicapp.com so the tab still has something to show.
const MOSAIC_DOMAIN = 'mosaicapp.com';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 25;

type SuppressedEmailsTabProps = {
  teamId: number;
};

const formatLastUpdate = (iso: string): string => {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return format(parsed, 'yyyy-MM-dd HH:mm:ss');
};

const reasonChipColor = (reason: string): 'error' | 'warning' | 'default' => {
  const upper = reason.toUpperCase();
  if (upper === 'BOUNCE') return 'error';
  if (upper === 'COMPLAINT') return 'warning';
  return 'default';
};

const SuppressedEmailsTab = ({ teamId }: SuppressedEmailsTabProps) => {
  const { showSuccess, showInfo, showError } = useNotification();

  // Read team members from Redux — populated by `fetchTeamMembers` thunk
  // (GET /rails_api/teams/:teamId/team_memberships) which the team profile
  // page kicks off when this tab is active.
  const { allMembers, isFetchingMember } = useSelector(selectTeamMembers);

  const domains = useMemo(() => {
    const set = new Set<string>();
    for (const member of allMembers || []) {
      const email = member?.email;
      if (!email) continue;
      const at = email.lastIndexOf('@');
      if (at < 0 || at === email.length - 1) continue;
      const host = email
        .slice(at + 1)
        .toLowerCase()
        .trim();
      if (host && host !== MOSAIC_DOMAIN) set.add(host);
    }
    // If excluding mosaicapp.com leaves nothing, fall back to it so the tab
    // can still render something useful.
    if (set.size === 0) set.add(MOSAIC_DOMAIN);
    return Array.from(set).sort();
  }, [allMembers]);

  const {
    suppressedEmails,
    isLoading: suppressedLoading,
    isFetching: suppressedFetching,
  } = useListSuppressedEmailsQuery({
    domains,
    enabled: !isFetchingMember && !!teamId,
  });

  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [pendingDelete, setPendingDelete] = useState<SuppressedEmail | null>(null);

  // Reset to first page when the search query changes.
  useEffect(() => {
    setPage(0);
  }, [searchInput]);

  const { deleteSuppressedEmailAsync, isDeleting } = useDeleteSuppressedEmailMutation();

  const filteredRows = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return suppressedEmails;
    return suppressedEmails.filter((row) => row.emailAddress.toLowerCase().includes(q));
  }, [searchInput, suppressedEmails]);

  // Reset to page 0 when the filtered set shrinks past the current page.
  const totalRows = filteredRows.length;
  const maxPage = Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);

  const paginatedRows = useMemo(() => {
    const start = safePage * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, safePage, rowsPerPage]);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    try {
      const response = await deleteSuppressedEmailAsync({
        email: target.emailAddress,
      });
      if (response?.data?.removed) {
        showSuccess(`Removed ${target.emailAddress} from the suppression list.`);
      } else {
        showInfo(`${target.emailAddress} was not on the suppression list.`);
      }
    } catch {
      showError(`Failed to remove ${target.emailAddress}.`);
    } finally {
      setPendingDelete(null);
    }
  };

  const showSpinner = isFetchingMember || suppressedLoading || suppressedFetching;

  return (
    <Box
      sx={{
        mt: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            maxWidth: 400,
          }}
        >
          <Paper variant="outlined" sx={{ borderRadius: 1 }}>
            <SearchBar inputValue={searchInput} setInputValue={setSearchInput} />
          </Paper>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {isFetchingMember
            ? 'Resolving domains…'
            : `Querying ${domains.length} domain${domains.length === 1 ? '' : 's'} • ${suppressedEmails.length} suppressed`}
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email Address</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right" width={80}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSpinner && paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box
                    sx={{
                      py: 4,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {!showSpinner && paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box
                    sx={{
                      py: 4,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {searchInput
                        ? `No matches for "${searchInput}".`
                        : 'No suppressed emails for these domains.'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {paginatedRows.map((row) => (
              <TableRow key={row.emailAddress} hover>
                <TableCell>{row.emailAddress}</TableCell>
                <TableCell>
                  {row.reason ? (
                    <Chip
                      size="small"
                      label={row.reason}
                      color={reasonChipColor(row.reason)}
                      variant="outlined"
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{formatLastUpdate(row.lastUpdateTime)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Remove from suppression list">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setPendingDelete(row)}
                        disabled={isDeleting}
                        aria-label={`Remove ${row.emailAddress}`}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRows}
          page={safePage}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </TableContainer>

      <SimpleModal
        open={!!pendingDelete}
        onClose={() => (isDeleting ? undefined : setPendingDelete(null))}
        headerText="Remove from suppression list"
        Body={
          <Box>
            <Typography>
              Remove <strong>{pendingDelete?.emailAddress}</strong> from the SES suppression list?
              Future emails to this address will be allowed again.
            </Typography>
          </Box>
        }
        confirmButtonText={isDeleting ? 'Removing…' : 'Remove'}
        cancelButtonText="Cancel"
        disableConfirmButton={isDeleting}
        disableCancelButton={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default SuppressedEmailsTab;
